import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  Theme,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { Warning } from '@bb-ui/icons/dist/small/Warning';
import { Clear } from '@bb-ui/icons/dist/small/Clear';

interface SiteConsistencyErrorDialogProps {
  open: boolean;
  onClose: () => void;
  onRemoveIncompatible: () => void;
  onRemoveSite: (siteId: string) => void;
  requiredFleet?: string;
  requiredAccount?: string;
  incompatibleSites?: string[];
  allSelectedSites?: Array<{
    captain_id: string;
    fleet_name?: string;
    aws_account?: string;
  }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '700px',
    maxWidth: '900px',
  },
  warningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    color: theme.palette.warning.main,
  },
  warningIcon: {
    fontSize: '24px',
  },
  contentSection: {
    marginBottom: theme.spacing(2),
  },
  requirementBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  sitesTableContainer: {
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  sitesTable: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(1, 2),
      fontSize: '0.875rem',
    },
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  siteRow: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  siteIdCell: {
    fontFamily: 'monospace',
    fontWeight: 500,
  },
  matchingCell: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  mismatchCell: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  normalCell: {
    color: theme.palette.text.primary,
  },
  actionCell: {
    width: '80px',
    textAlign: 'center' as const,
  },
  removeIconButton: {
    color: theme.palette.error.main,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.error.dark,
      backgroundColor: 'transparent',
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1.5),
  },
  doneButton: {
    backgroundColor: '#4caf50',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  removeButton: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

const SiteConsistencyErrorDialog: React.FC<SiteConsistencyErrorDialogProps> = ({
  open,
  onClose,
  onRemoveIncompatible,
  onRemoveSite,
  requiredFleet,
  requiredAccount,
  incompatibleSites = [],
  allSelectedSites = [],
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const sitesWithStatus = React.useMemo(() => {
    if (requiredFleet || requiredAccount) {
      return allSelectedSites.map((site) => {
        const fleetMatches = !requiredFleet || site.fleet_name === requiredFleet;
        const accountMatches = !requiredAccount || site.aws_account === requiredAccount;
        const isCompatible = fleetMatches && accountMatches;
        const isIncompatible = incompatibleSites.includes(site.captain_id);

        return {
          captain_id: site.captain_id,
          fleet_name: site.fleet_name,
          aws_account: site.aws_account,
          isMajority: isCompatible,
          isIncompatible,
          fleetMatches,
          accountMatches,
        };
      });
    }

    const groupCounts = new Map<string, { count: number; sites: typeof allSelectedSites }>();

    allSelectedSites.forEach((site) => {
      const key = `${site.fleet_name || 'unknown'}-${site.aws_account || 'unknown'}`;
      const existing = groupCounts.get(key);
      if (existing) {
        existing.count += 1;
        existing.sites.push(site);
      } else {
        groupCounts.set(key, { count: 1, sites: [site] });
      }
    });

    let majorityKey = '';
    let maxCount = 0;
    Array.from(groupCounts.entries()).forEach(([key, group]) => {
      if (group.count > maxCount) {
        maxCount = group.count;
        majorityKey = key;
      }
    });

    if (maxCount === 0 && allSelectedSites.length > 0) {
      const firstSite = allSelectedSites[0];
      majorityKey = `${firstSite.fleet_name || 'unknown'}-${firstSite.aws_account || 'unknown'}`;
    }

    return allSelectedSites.map((site) => {
      const siteKey = `${site.fleet_name || 'unknown'}-${site.aws_account || 'unknown'}`;
      const isMajority = siteKey === majorityKey;
      const groupData = groupCounts.get(siteKey);
      const isIncompatible = incompatibleSites.includes(site.captain_id);

      return {
        captain_id: site.captain_id,
        fleet_name: site.fleet_name,
        aws_account: site.aws_account,
        isMajority,
        isIncompatible,
        groupSize: groupData?.count || 0,
        fleetMatches: isMajority,
        accountMatches: isMajority,
      };
    });
  }, [allSelectedSites, requiredFleet, requiredAccount, incompatibleSites]);

  const majorityGroup = React.useMemo(() => {
    if (requiredFleet || requiredAccount) {
      return {
        fleet: requiredFleet,
        account: requiredAccount,
        count: sitesWithStatus.filter((s) => s.isMajority).length,
      };
    }

    const majority = sitesWithStatus.find((site) => site.isMajority);
    return majority
      ? {
          fleet: majority.fleet_name,
          account: majority.aws_account,
          count: (majority as any).groupSize || 0,
        }
      : null;
  }, [sitesWithStatus, requiredFleet, requiredAccount]);

  const minorityGroups = React.useMemo(() => {
    const groups = new Map<string, { fleet?: string; account?: string; count: number }>();

    sitesWithStatus.forEach((site) => {
      if (!site.isMajority) {
        const key = `${site.fleet_name}-${site.aws_account}`;
        if (!groups.has(key)) {
          groups.set(key, {
            fleet: site.fleet_name,
            account: site.aws_account,
            count: (site as any).groupSize || 1,
          });
        }
      }
    });

    return Array.from(groups.values());
  }, [sitesWithStatus]);

  const allSitesConsistent = React.useMemo(() => {
    if (allSelectedSites.length <= 1) return true;

    if (requiredFleet || requiredAccount) {
      return sitesWithStatus.every((site) => site.isMajority);
    }

    const firstSite = allSelectedSites[0];
    const referenceKey = `${firstSite?.fleet_name || 'unknown'}-${firstSite?.aws_account || 'unknown'}`;

    return allSelectedSites.every((site) => {
      const siteKey = `${site.fleet_name || 'unknown'}-${site.aws_account || 'unknown'}`;
      return siteKey === referenceKey;
    });
  }, [allSelectedSites, sitesWithStatus, requiredFleet, requiredAccount]);

  const handleRemoveSite = (siteId: string) => {
    onRemoveSite(siteId);
  };

  const handleRemoveAllIncompatible = () => {
    onRemoveIncompatible();
    if (allSitesConsistent) {
      onClose();
    }
  };

  const handleDone = () => {
    onClose();
  };

  const getCellClassName = (site: (typeof sitesWithStatus)[0], field: 'fleet' | 'account') => {
    if (field === 'fleet') {
      if (site.fleetMatches) return classes.matchingCell;
      if (site.isIncompatible) return classes.mismatchCell;
      return classes.normalCell;
    }

    if (site.accountMatches) return classes.matchingCell;
    if (site.isIncompatible) return classes.mismatchCell;
    return classes.normalCell;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle onClose={onClose} closeLabel={t('global.close')}>
        {t('cohorts.siteConsistency.title')}
      </DialogTitle>

      <DialogContent>
        <div className={classes.warningHeader}>
          <Warning className={classes.warningIcon} />
          <Typography variant="h6">{t('cohorts.siteConsistency.header')}</Typography>
        </div>

        <div className={classes.contentSection}>
          <Typography variant="body1" gutterBottom>
            {t('cohorts.siteConsistency.description')}
          </Typography>
        </div>

        {majorityGroup && (
          <div className={classes.requirementBox}>
            <Typography variant="body2" gutterBottom style={{ fontWeight: 600 }}>
              {requiredFleet || requiredAccount
                ? t('cohorts.siteConsistency.requiredConfig')
                : t('cohorts.siteConsistency.majorityConfig')}{' '}
              ({majorityGroup.count} sites):
            </Typography>
            <Typography variant="body2">
              <strong>{t('cohorts.siteFleet')}:</strong>{' '}
              {majorityGroup.fleet || t('cohorts.notSpecified')}
            </Typography>
            <Typography variant="body2">
              <strong>{t('cohorts.awsAccount')}:</strong>{' '}
              {majorityGroup.account || t('cohorts.notSpecified')}
            </Typography>
            {minorityGroups.length > 0 && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 8, display: 'block' }}
              >
                {t('cohorts.siteConsistency.alternativeConfigs')}
              </Typography>
            )}
          </div>
        )}

        {sitesWithStatus && sitesWithStatus.length > 0 && (
          <div className={classes.sitesTableContainer}>
            <Typography
              variant="body2"
              gutterBottom
              style={{ fontWeight: 600, padding: '16px 16px 8px 16px' }}
            >
              {t('cohorts.siteConsistency.sitesComparison')}
            </Typography>
            <Table size="small" className={classes.sitesTable}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>{t('cohorts.captainId')}</TableCell>
                  <TableCell className={classes.tableHeader}>{t('cohorts.siteFleet')}</TableCell>
                  <TableCell className={classes.tableHeader}>{t('cohorts.awsAccount')}</TableCell>
                  <TableCell className={classes.tableHeader}>
                    {t('cohorts.siteConsistency.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sitesWithStatus.map((site) => (
                  <TableRow key={site.captain_id} className={classes.siteRow}>
                    <TableCell className={classes.siteIdCell}>{site.captain_id}</TableCell>
                    <TableCell className={getCellClassName(site, 'fleet')}>
                      {site.fleet_name || '-'}
                    </TableCell>
                    <TableCell className={getCellClassName(site, 'account')}>
                      {site.aws_account || '-'}
                    </TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSite(site.captain_id)}
                        className={classes.removeIconButton}
                        aria-label={t('cohorts.siteConsistency.removeSite')}
                      >
                        <Clear />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            {allSitesConsistent
              ? t('cohorts.siteConsistency.allConsistent')
              : t('cohorts.siteConsistency.instruction')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <DefaultButton onClick={onClose}>
          {t('cohorts.siteConsistency.buttons.cancel')}
        </DefaultButton>

        {!allSitesConsistent && incompatibleSites.length > 0 && (
          <PrimaryButton onClick={handleRemoveAllIncompatible} className={classes.removeButton}>
            {t('cohorts.siteConsistency.buttons.removeIncompatible', {
              count: incompatibleSites.length,
              plural: incompatibleSites.length !== 1 ? 's' : '',
            })}
          </PrimaryButton>
        )}

        {allSitesConsistent && (
          <PrimaryButton onClick={handleDone} className={classes.doneButton}>
            {t('cohorts.siteConsistency.buttons.done')}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SiteConsistencyErrorDialog;
